/**
 * Converts timeline time (seconds) to pixels.
 */
export const timeToPixel = (
  timeInSeconds: number,
  pixelsPerSecond: number,
  zoom: number,
): number => timeInSeconds * pixelsPerSecond * zoom;

/**
 * Converts pixels to timeline time (seconds).
 */
export const pixelToTime = (
  pixel: number,
  pixelsPerSecond: number,
  zoom: number,
): number => {
  if (pixelsPerSecond <= 0 || zoom <= 0) {
    return 0;
  }
  return pixel / (pixelsPerSecond * zoom);
};

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Formats seconds as `M:SS` or `H:MM:SS`.
 */
export const formatTime = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(
      remainingSeconds,
    ).padStart(2, '0')}`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

/**
 * Formats seconds as `M:SS.mmm` or `H:MM:SS.mmm`.
 */
export const formatTimeWithMs = (seconds: number): string => {
  const safeSeconds = Math.max(0, seconds);
  const wholeSeconds = Math.floor(safeSeconds);
  const ms = Math.round((safeSeconds - wholeSeconds) * 1000);
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const remainingSeconds = wholeSeconds % 60;
  const msLabel = String(ms).padStart(3, '0');

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(
      remainingSeconds,
    ).padStart(2, '0')}.${msLabel}`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}.${msLabel}`;
};

export const getTimelineWidth = (
  durationInSeconds: number,
  pixelsPerSecond: number,
  zoom: number,
): number => timeToPixel(durationInSeconds, pixelsPerSecond, zoom);
